import {
    Building2,
    Plus,
    ChevronDown,
    Globe,
    Linkedin,
    ArrowUpRight,
    Star,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { cn, getStageText } from "@/lib/utils";
import Flag from "react-world-flags";
import lookup from "country-code-lookup";
import { Company } from "@/hooks/use-deal-radar";

interface CompanyCardProps {
    company: Company;
    rank: number;
    onAddToPipeline: (companyId: string) => void;
    isAddingToPipeline?: boolean;
    isExpanded: boolean;
    onToggle: () => void;
}

const stageLabelFromValue = (stage?: number | string | null) => {
    if (stage === undefined || stage === null || stage === "" || stage === 0) return "–";
    return getStageText(stage);
};

const formatDate = (dateString?: string) => {
    if (!dateString) return "–";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "–";
    return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};

export function CompanyCard({
    company,
    rank,
    onAddToPipeline,
    isAddingToPipeline,
    isExpanded,
    onToggle,
}: CompanyCardProps) {

    // Helper functions (reused logic from index.tsx)
    const getAvatarUrl = (c: Company) =>
        c.company_file_ids?.find((f) => f.fileType === "company avatar")?.fileId;

    const getPitchDeckUrl = (c: Company) =>
        c.company_file_ids?.find((f) => f.fileType === "company pitchdeck")?.fileId;

    const getPitchDeckViewerUrl = (url?: string) => {
        if (!url) return null;
        try {
            return `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
        } catch {
            return url;
        }
    };

    const avatarUrl = getAvatarUrl(company);
    const pitchDeckUrl = getPitchDeckUrl(company);
    const pitchDeckViewerUrl = getPitchDeckViewerUrl(pitchDeckUrl);
    const stageLabel = stageLabelFromValue(company.stage);
    const addedDate = formatDate(company.created_on);
    const isHighlighted = company.deal_radar_highlight;

    return (
        <div
            className={cn(
                "bg-card rounded-xl border p-4 transition-all duration-200",
                isHighlighted && "border-2 border-amber-400 bg-amber-50/30 dark:bg-amber-950/20",
                isExpanded && !isHighlighted && "border-blue-500 shadow-md",
                isExpanded && isHighlighted && "shadow-md shadow-amber-200/50 dark:shadow-amber-900/30",
                !isExpanded && !isHighlighted && "border-border"
            )}
            onClick={onToggle}
        >
            {/* Collapsed State */}
            <div className="flex items-start gap-3">
                {/* Logo (Left) */}
                <div className="flex-shrink-0 h-12 w-12 rounded-lg bg-muted flex items-center justify-center overflow-hidden border border-border/50">
                    {avatarUrl ? (
                        <img
                            src={avatarUrl}
                            alt={`${company.company_name} logo`}
                            className="h-full w-full object-cover"
                        />
                    ) : (
                        <Building2 className="h-6 w-6 text-muted-foreground" />
                    )}
                </div>

                {/* Center Info */}
                <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-mono text-muted-foreground mb-0.5">#{rank}</span>
                        <h3 className="font-semibold text-base truncate pr-1 flex items-center gap-1.5">
                            {isHighlighted && (
                                <Star className="h-4 w-4 text-amber-500 fill-amber-400 flex-shrink-0" />
                            )}
                            {company.company_name}
                        </h3>
                    </div>

                    <div className="flex flex-wrap gap-1.5 items-center">
                        {company.country && lookup.byCountry(company.country)?.iso2 && (
                            <div className="flex items-center gap-1 bg-muted/50 px-1.5 py-0.5 rounded text-xs text-muted-foreground">
                                <div className="w-3.5 flex-shrink-0">
                                    <Flag
                                        code={lookup.byCountry(company.country)?.iso2}
                                        className="w-full h-auto shadow-sm rounded-[1px]"
                                        style={{ display: "block" }}
                                    />
                                </div>
                                <span className="truncate max-w-[60px]">{company.country}</span>
                            </div>
                        )}

                        <Badge variant="secondary" className="text-[10px] px-1.5 h-5 font-medium border-0 bg-blue-500/10 text-blue-500 hover:bg-blue-500/20">
                            {stageLabel}
                        </Badge>
                    </div>

                    <div className="flex flex-wrap gap-1 mt-1.5">
                        {(company.industry || []).slice(0, 2).map((ind) => (
                            <span key={ind} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-muted text-muted-foreground">
                                {ind}
                            </span>
                        ))}
                        {(company.industry?.length || 0) > 2 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-muted text-muted-foreground">
                                +{(company.industry?.length || 0) - 2}
                            </span>
                        )}
                    </div>

                    <div className="text-[10px] text-muted-foreground pt-0.5">
                        <span className="opacity-70">Added:</span> <span className="font-medium">{addedDate}</span>
                    </div>
                </div>

                {/* Right Actions */}
                <div className="flex items-center gap-2 pt-1 pl-1">
                    <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 rounded-md bg-secondary/50 hover:bg-secondary text-foreground shrink-0"
                        onClick={(e) => {
                            e.stopPropagation();
                            onAddToPipeline(company._id);
                        }}
                        disabled={isAddingToPipeline}
                    >
                        <Plus className="h-4 w-4" />
                    </Button>

                    <ChevronDown
                        className={cn(
                            "h-5 w-5 text-muted-foreground transition-transform duration-300",
                            isExpanded && "rotate-180 text-blue-500"
                        )}
                    />
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="mt-4 pt-4 border-t border-border/50 animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="text-sm text-foreground/80 leading-relaxed mb-4">
                        {company.short_bio || "No description available."}
                    </div>

                    <div className="flex flex-col gap-2.5">
                        {company.company_website && (
                            <Button variant="outline" className="w-full justify-center h-10 border-border/60 bg-muted/5 hover:bg-muted/20" asChild>
                                <a href={company.company_website} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                                    <Globe className="h-4 w-4 mr-2 opacity-70" /> Website
                                </a>
                            </Button>
                        )}

                        {company.linkedIn && (
                            <Button variant="outline" className="w-full justify-center h-10 border-border/60 bg-muted/5 hover:bg-muted/20" asChild>
                                <a href={company.linkedIn} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                                    <Linkedin className="h-4 w-4 mr-2 opacity-70" /> LinkedIn
                                </a>
                            </Button>
                        )}

                        {pitchDeckViewerUrl && (
                            <Button className="w-full justify-center h-10 bg-blue-600 hover:bg-blue-700 text-white shadow-sm" asChild>
                                <a href={pitchDeckViewerUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                                    <ArrowUpRight className="h-4 w-4 mr-2" />
                                    View Pitch Deck
                                </a>
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
